<template>
  <!--
    Panel de certificaciones (pictogramas) reutilizable, dirigido por `entityType`:
      2 = montaje mecánico, 3 = instalación eléctrica.
    Orquesta: filtros (CertificationFilter) + tabla (CertificationTable) + los
    diálogos "ver certificación actual" y "generar certificación", todos servidos
    por UNA sola instancia de ModalWrapper (carga cualquier componente).
  -->
  <div>
    <!-- Filtros (componente extraído; filtra en cliente sobre groupedCertifications) -->
    <certification-filter
      :filters.sync="filters"
      :subcontractors="subcontractors"
      :work-orders="workOrders"
      @clear="clearFilters"
    />

    <certification-table
      :certifications="displayedCertifications"
      :loading="isLoadingGrouped"
      :project-id="projectId"
      @view-current="openViewCurrentCertificationModal"
      @reload="onReload"
    >
      <!-- Acciones del header (barra epc-data-table__summary): ver-actual / generar. -->
      <template #actions>
        <b-button type="is-light" size="is-small" icon-left="eye" icon-pack="fas" @click="openViewCurrentCertificationModal">
          {{ $t('view_current_certification') }}
        </b-button>
        <b-button type="is-primary" size="is-small" icon-left="plus" icon-pack="fas" @click="openGenerateCertificationModal">
          {{ $t('generate_certification') }}
        </b-button>
      </template>
    </certification-table>

    <!-- Modal genérico (EpcModalWrapper, modo imperativo): hospeda ver-actual y generar. -->
    <epc-modal-wrapper ref="modal" />
  </div>
</template>

<script>
import axios from 'axios'
import moment from 'moment'
import CertificationFilter from './CertificationFilter.vue'
import CertificationTable from './CertificationTable.vue'
import EpcModalWrapper from '·/components/epc-modal-wrapper.vue'
import CertificationFormGenerateSolaring from './forms/CertificationFormGenerateSolaring.vue'
import CertificationFormCurrent from './forms/CertificationFormCurrent.vue'

// Etiquetas de entidad para el título del diálogo "Generar certificación de …".
const ENTITY_LABELS = {
  1: 'entity_civil_works',
  2: 'entity_mechanical_assembly',
  3: 'entity_electrical_installation',
}

export default {
  name: 'CertificationPanel',

  components: {
    CertificationFilter,
    CertificationTable,
    EpcModalWrapper,
  },

  props: {
    projectId: { type: Number, default: null },
    // Entidad que se certifica (PartnerProcessEntity): 2=mecánico, 3=eléctrico.
    entityType: { type: Number, required: true },
  },

  data: () => ({
    filters: {
      id: null,
      subcontractor: null,
      workOrder: null,
      status: null,
      date: null,
    },
    subcontractors: [],
    workOrders: [],
    groupedCertifications: [],
    isLoadingGrouped: false,
  }),

  computed: {
    // Filtrado en cliente (subcontratista, pedido, id/código, estado y fecha).
    // Reactivo: se recalcula al mutar cualquier filtro desde CertificationFilter.
    displayedCertifications() {
      return this._FilterGroupedCertifications(this.groupedCertifications, this.filters)
    },
    // Título del diálogo "Generar certificación de <entidad>".
    generateTitle() {
      const entityKey = ENTITY_LABELS[this.entityType]
      if (!entityKey) {
        return this.$t('generate_certification')
      }
      return this.$t('generate_certification_of', [this.$t(entityKey)])
    },
  },

  created() {
    this.loadGroupedCertifications()
  },

  methods: {
    async loadGroupedCertifications(options = {}) {
      if (!this.projectId) {
        return
      }
      const silent = options.silent === true
      if (!silent) {
        this.isLoadingGrouped = true
      }
      try {
        const params = {
          entity_type: this.entityType,
          page: 1,
          per_page: 100,
        }
        if (this.filters.subcontractor) params.partner_id = this.filters.subcontractor
        if (this.filters.workOrder) params.work_order_id = this.filters.workOrder

        const { data } = await axios.get(
          `/api/v2/project/${this.projectId}/certifications/grouped-by-order`,
          { params }
        )
        this.groupedCertifications = (data && data.data) ? data.data : []
        this._RefreshFilterOptions()
      } catch (e) {
        this.groupedCertifications = []
        this.$notify.error('error_loading_data')
      } finally {
        if (!silent) {
          this.isLoadingGrouped = false
        }
      }
    },

    _RefreshFilterOptions() {
      const subMap = new Map()
      const orderMap = new Map()
      this.groupedCertifications.forEach((row) => {
        if (row.partner_id && !subMap.has(row.partner_id)) {
          subMap.set(row.partner_id, { id: row.partner_id, name: row.partner_name })
        }
        if (row.work_order_id && !orderMap.has(row.work_order_id)) {
          orderMap.set(row.work_order_id, { id: row.work_order_id, code: row.work_order_code })
        }
      })
      this.subcontractors = Array.from(subMap.values())
      this.workOrders = Array.from(orderMap.values())
    },

    // Filtra los grupos en cliente por los 5 filtros (mismo criterio que mecánico/eléctrico).
    _FilterGroupedCertifications(rows, f) {
      let out = rows || []
      if (f.subcontractor) {
        out = out.filter(r => Number(r.partner_id) === Number(f.subcontractor))
      }
      if (f.workOrder) {
        out = out.filter(r => Number(r.work_order_id) === Number(f.workOrder))
      }
      if (f.id !== null && f.id !== '' && f.id !== undefined) {
        const q = String(f.id).toLowerCase().trim()
        out = out.filter(r =>
          String(r.id).toLowerCase().includes(q) ||
          String(r.work_order_code || '').toLowerCase().includes(q) ||
          (r.certifications || []).some(c => String(c.certification || '').toLowerCase().includes(q))
        )
      }
      if (f.status !== null && f.status !== undefined && f.status !== '') {
        out = out.filter(r => (r.certifications || []).some(c => Number(c.status) === Number(f.status)))
      }
      if (f.date) {
        const d = moment(f.date).format('YYYY-MM-DD')
        out = out.filter(r => (r.certifications || []).some(c =>
          (c.date_from && c.date_to && d >= c.date_from && d <= c.date_to) || c.created_at === d
        ))
      }
      return out
    },

    clearFilters() {
      this.filters.id = null
      this.filters.subcontractor = null
      this.filters.workOrder = null
      this.filters.status = null
      this.filters.date = null
      this.loadGroupedCertifications()
    },

    // Abre "Ver certificación actual" en el ModalWrapper compartido.
    // prefill = { partnerId, orderId } cuando viene del icono de una fila (@view-current);
    // desde el botón del header llega el MouseEvent (sin partnerId) → sin prefill.
    openViewCurrentCertificationModal(prefill = {}) {
      this.$refs.modal.show({
        component: CertificationFormCurrent,
        title: this.$t('view_current_certification'),
        width: 640,
        // Evita que la caja del modal recorte el desplegable del multiselect.
        overflowVisible: true,
        props: {
          projectId: this.projectId,
          entityType: this.entityType,
          initialPartnerId: prefill.partnerId || null,
          initialOrderId: prefill.orderId || null,
        },
      })
    },

    // Abre "Generar certificación" (formulario sin chrome) en el mismo ModalWrapper.
    // Ancho dinámico por CSS (epc-modal--wide = min(90vw, 1400px)); se adapta al
    // viewport sin fijar un valor en px.
    openGenerateCertificationModal() {
      this.$refs.modal.show({
        component: CertificationFormGenerateSolaring,
        title: this.generateTitle,
        // El cuerpo scrollea dentro del diálogo (tope 92vh): a 100% de zoom el form es más alto
        // que el viewport y sin esto quedaban cortadas Observaciones + botones Cancelar/Crear.
        // Los dropdowns no se recortan porque los boxes internos usan overflow-visible con el
        // formulario abierto (escapan al cuerpo, mucho más alto que la ventanita de 320px).
        scrollBody: true,
        overflowVisible: true,
        contentClass: 'epc-modal--wide',
        props: {
          projectId: this.projectId,
          entityType: this.entityType,
        },
        // El form emite 'created' con el payload; 'close' lo cierra el wrapper solo.
        listeners: {
          created: this.onCertificationCreated,
        },
      })
    },

    // Tras crear, recargamos desde el mismo endpoint filtrado por entity_type.
    onCertificationCreated() {
      this.loadGroupedCertifications()
    },

    // La tabla pide recargar tras regenerar/retirar (recarga silenciosa).
    onReload(options = {}) {
      this.loadGroupedCertifications({ silent: options.silent === true })
    },
  },
}
</script>

<style lang="scss" scoped>
</style>
